import styled from "@emotion/styled";
import CancelIcon from "@mui/icons-material/Cancel";
import ColorizeIcon from "@mui/icons-material/Colorize";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import {
  Box,
  Button,
  type ButtonBaseProps,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import { useContrastBorder } from "hooks/contrast-border";
import {
  type Dispatch,
  type FC,
  type MouseEvent,
  useRef,
  useState,
} from "react";
import { type RgbColor, RgbColorPicker } from "react-colorful";
import { formatColorText } from "service/color-formatting";
import {
  type ColorCieLab,
  type ColorRgb,
  labToRgb,
  rgbToCss,
  rgbToLab,
} from "service/color-math";
import type { NamedColor } from "../../filter/color-filter/ColorFilter";

export interface ColorRgbButtonProps {
  color?: ColorCieLab;
  onChange?: Dispatch<NamedColor>;
  onDelete?: Dispatch<void>;
  sx?: ButtonBaseProps["sx"];
  disabled?: boolean;
  t: (key: string) => string;
}

const ColorPreviewButton = styled(Button)`
  width: 48px;
  height: 48px;
  min-width: 0;
  padding: 0;
  transition: none;
  border-radius: 50%;
`;

const defaultColor: ColorCieLab = [210, 210, 210];
export const ColorRgbButton: FC<ColorRgbButtonProps> = ({
  color,
  onChange,
  onDelete,
  sx,
  disabled = false,
  t,
}) => {
  const anchorRef = useRef<any>();
  const [visible, setIsVisible] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [storedColor, setStoredColor] = useState<ColorRgb>(
    color ? labToRgb(color) : defaultColor,
  );

  const cssColor = rgbToCss(storedColor);
  const contrastBorder = useContrastBorder(rgbToLab(storedColor));

  if (storedColor === defaultColor && disabled) {
    return (
      <Tooltip title={t("color_tooltip_preview_unavailable")}>
        <span>
          <ColorPreviewButton
            disabled
            sx={{
              ...sx,
              border: "2px dashed",
            }}
          >
            <FormatColorResetIcon />
          </ColorPreviewButton>
        </span>
      </Tooltip>
    );
  }

  const handleClose = () => {
    setIsVisible(false);
    if (hasChanged) {
      const value = rgbToLab(storedColor);
      onChange?.({
        name: formatColorText(value),
        value: value,
      });
    }
  };

  const onColorDelete = (e: MouseEvent<any>) => {
    e.stopPropagation();
    onDelete?.();
  };

  const onColorChange = (color: RgbColor) => {
    setHasChanged(true);
    setStoredColor([color.r, color.g, color.b]);
  };

  return (
    <Box position="relative">
      <Tooltip
        title={
          disabled
            ? `${t("color_tooltip_preview")}`
            : `${t("color_tooltip_open")}`
        }
      >
        <span>
          <ColorPreviewButton
            ref={anchorRef}
            disabled={disabled}
            onClick={() => setIsVisible(true)}
            sx={{
              ...sx,
              backgroundColor: disabled ? cssColor : "#B2E2F7",
              "&:hover": {
                backgroundColor: disabled ? cssColor : "#B2E2F7",
                "&:not([disabled])": {
                  outline: "8px solid rgba(178, 226, 247, 0.4)",
                },
              },
              border: contrastBorder,
            }}
          >
            {!disabled && (
              <ColorizeIcon
                sx={(theme) => ({
                  color: contrastBorder
                    ? theme.palette.grey[400]
                    : "rgba(255,255,255,0.9)",
                })}
              />
            )}
          </ColorPreviewButton>
        </span>
      </Tooltip>
      <IconButton
        size="small"
        title={t("color_tooltip_remove_btn")}
        onClick={onColorDelete}
        sx={(theme) => ({
          position: "absolute",
          top: -8,
          right: -8,
          padding: "1px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.grey[400],
          "&:hover": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.grey[500],
          },
        })}
      >
        <CancelIcon />
      </IconButton>
      <Popover
        open={visible}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box padding={2}>
          <RgbColorPicker
            style={{ width: 200, height: 200 }}
            onChange={onColorChange}
            color={{ r: storedColor[0], g: storedColor[1], b: storedColor[2] }}
          />
        </Box>
      </Popover>
    </Box>
  );
};
